import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ImageUpload from "src/Components/GlobalComponents/ImageUplaod";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import httpRequest from "src/helpers/httpRequest";
import {
  IHomecareService,
  IHomePageInterface,
  ITagsItems,
} from "src/types/GlobalInterfaces";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import { IPaginated } from "src/types/paginated";
import SimpleInput from "src/Components/SimpleInput";
import CustomWebsiteDropdown from "src/Components/GlobalComponents/Widget/CustomWebsiteDropdown";

interface ProductCategoryType {
  mainCategoryID: string;
  serviceID: string;
  key: string;
}

interface ICategoryResponse {
  documents: any[];
  paginated: IPaginated;
}

const HomecareServiceSection: React.FC<{
  allCategories: any[];
  allMainCategory: any[];
  data: {
    heading: string;
    services: {
      mainCategoryId: string;
      serviceId: string;
    }[];
  } | null;
  setData: React.Dispatch<React.SetStateAction<IHomePageInterface | null>>;
}> = ({ allCategories, allMainCategory, data, setData }) => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "platformManagement", false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [heading, setHeading] = useState<string>("");
  const [allServiceCategories, setAllServiceCategories] = useState<any[]>([]);
  const [serviceCategoryLoader, setServiceCategoryLoader] =
    useState<boolean>(false);
  const [productCategory, setProductCategory] = useState<ProductCategoryType[]>(
    Array(8)
      .fill(null)
      .map((_, index) => ({
        key: `product${String(index + 1).padStart(2, "0")}`,
        mainCategoryID: "",
        serviceID: "",
      }))
  );

  const getAllCategories = async () => {
    setServiceCategoryLoader(true);
    const { res, err } = await httpRequest<any>({
      path: `/home-care-services/getAll?limit=1000`,
    });
    if (res) {
      setAllServiceCategories(res?.documents);
    } else {
      toast(err, { type: "error" });
    }
    setServiceCategoryLoader(false);
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  const handleSubmit = async () => {
    if (!hasWriteAccess) {
      return;
    }
    if (!heading) {
      toast("Heading is required", { type: "error" });
      return;
    }

    if (productCategory.some((tm) => !tm?.serviceID)) {
      toast("Please select all homecare services", { type: "error" });
      return;
    }
    setIsLoader(true);

    const payload: any = {
      heading: heading,
      services:
        productCategory?.map((tm) => ({
          mainCategoryId: tm?.mainCategoryID,
          serviceId: tm?.serviceID,
        })) || null,
    };

    let { res, err } = await httpRequest({
      path: "/website-manage/section-six",
      method: "put",
      params: payload,
    });

    if (res) {
      toast("Homecare service updated successfully", { type: "success" });
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    if (data?.services) {
      setProductCategory(
        data.services
          .map((tm, ind) => ({
            mainCategoryID: tm?.mainCategoryId || "",
            serviceID: tm?.serviceId || "",
            key: `product${String(ind + 1).padStart(2, "0")}`,
          }))
          .concat(
            Array(8 - (data.services.length || 0))
              .fill(null)
              .map((_, ind) => ({
                mainCategoryID: "",
                serviceID: "",
                key: `product${String(data.services.length + ind + 1).padStart(
                  2,
                  "0"
                )}`,
              }))
          )
      );
      setHeading(data?.heading || "");
    } else {
      setProductCategory(
        Array(8)
          .fill(null)
          .map((_, index) => ({
            mainCategoryID: "",
            serviceID: "",
            key: `product${String(index + 1).padStart(2, "0")}`,
          }))
      );
      setHeading("");
    }
  }, [data]);
  const CheckDuplicates = (tm: IHomecareService, index: number) => {
    let allData = productCategory.filter((t, i) => i !== index);
    return allData.find((t) => t.serviceID === tm._id) ? true : false;
  };
  return (
    <div className="section__1">
      <h1 className="font-Poppins-SemiBold font-size-25px mb-2 theme-color-green">
        Section 06
      </h1>
      <div className="products-tabs-content">
        <div>
          <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
            Heading
          </h1>
          <SimpleInput
            labelText="Heading"
            placeholder="Type Heading"
            value={heading}
            onChange={(value: string) => setHeading(value)}
            id="homecare-service-heading"
            disabled={!hasWriteAccess}
          />
          <div className="w-full grid grid-cols-4 gap-4 gap-x-10">
            {productCategory.map((item, ind) => {
              return (
                <div className="w-full" key={item.key}>
                  <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
                    Product {String(ind + 1).padStart(2, "0")}
                  </h1>
                  {/* Main Category */}
                  <CustomWebsiteDropdown
                    loading={serviceCategoryLoader}
                    options={allCategories}
                    data={
                      item?.mainCategoryID
                        ? [
                            {
                              label:
                                allCategories.find(
                                  (tm) => tm.value === item?.mainCategoryID
                                )?.label || "",
                              value: item?.mainCategoryID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          mainCategoryID: val[0]?.value || "",
                          serviceID: "", // Reset serviceID when mainCategoryID changes
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select At-Home Category"
                    dropdownMainId={`homecare-service-main-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />
                  {/* Service */}
                  <CustomWebsiteDropdown
                    loading={serviceCategoryLoader}
                    options={allServiceCategories
                      .filter((tm) =>
                        tm?.categories.find(
                          (ite: { _id: string; name: string }) =>
                            ite?._id === item?.mainCategoryID
                        )
                      )
                      .map((tm) => ({
                        label: tm.serviceName,
                        value: tm._id,
                        disabled: CheckDuplicates(tm, ind),
                      }))}
                    data={
                      item?.serviceID
                        ? [
                            {
                              label:
                                allServiceCategories.find(
                                  (tm) => tm._id === item?.serviceID
                                )?.serviceName || "",
                              value: item?.serviceID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          serviceID: val[0]?.value || "",
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Homecare Service"
                    dropdownMainId={`homecare-service-service-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex mt-2 justify-end">
        <PrimaryBtn
          disabled={
            typeof productCategory === "object"
              ? false
              : true || !hasWriteAccess
          }
          loader={isLoader}
          ButtonText="Save"
          btnExtraStyle={{
            backgroundColor: "#00443f",
            borderRadius: "1.5rem",
          }}
          onPress={() => {
            if (!hasWriteAccess) {
              return;
            }
            handleSubmit();
          }}
          btnExtralClasses={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
          id="homecare-service-save"
        />
      </div>
    </div>
  );
};

export default HomecareServiceSection;
